import React from 'react';
import PropTypes from 'prop-types';
import { bind, compose } from 'underscore';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withScope } from '@bingads-webui-react/scope-service';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { FilterViewManagerWithGlobalFilterContext } from '@bingads-webui-campaign-react/filter-view-manager';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { download } from '@bingads-webui/download-service';
import { Button } from '@bingads-webui-react/primitive';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { AccountLabelMccReport } from '@bingads-webui-campaign/campaign-ui-schemas';

import { getDimensionsPageStyle } from './styles/dimensions-page-style';
import { createDimensionsGridForUiNext } from './dimensions-factory';
import { getToolbarConfig } from './toolbar-config';
import { DimensionsDataSource } from './dimensions-data-source';
import { AccountSelector } from './account-selector';

const StyledGridViewWrapperWithFilterContext = withGlobalFilter(StyledGridViewWrapper);

class DimensionsPage extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      permissions,
      scope,
    } = props;

    this.scope = scope;
    this.permissions = permissions;
    this.willUnMount = false;

    this.state = {
      selectedScope: [],
      backboneGridView: null,
    };

    this.createGrid();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.scope !== this.props.scope || prevProps.type !== this.props.type) {
      this.createGrid();
    }

    // when user switches report type from tree menu, toolbar could be forced hidden, e.g. column chooser was open
    // in this case column chooser did not get a chance to show toolbar on its dismiss
    // below action is needed to ensure toolbar is visible on new report
    if (prevProps.reportType !== this.props.reportType
      && this.props.forceHideToolbar
      && this.props.resetToolbarVisibility
    ) {
      this.props.resetToolbarVisibility({
        forceHideToolbar: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }
    this.willUnMount = true;
  }

  getHourOfDayErrorMessage() {
    return (
      <React.Fragment>
        <div className="alertsPanel alert alert-warning rg-alrtsPanel">
          <div className="alert-icon warning rg-alert-icon-warning iconba iconba-Warning" style={{ color: '#d90026' }} />
          <div style={{ 'margin-left': '3px' }}>
            {this.props.i18n.getString(_TL_('The Hour of Day dimension will only show data for the past 6 months.'))}
          </div>
        </div>
      </React.Fragment>
    );
  }

  getFilterBar = ({
    filterConfig,
    filterService,
    toolbarSelectedConfig,
    toolbarUnselectedConfig,
    updateToolbar,
    resetToolbarVisibility,
    newI18n,
    isMultiAccountPage,
  }) => {
    const toolbarEditor = getToolbarEditor({
      toolbarConfig: { toolbarSelectedConfig, toolbarUnselectedConfig },
      updateToolbar,
    });

    this.toolbarEditor = toolbarEditor;

    let updateToolbarBinded;
    if (this.permissions.dynamic && this.permissions.dynamic.ToolbarViewStateFilterPills) {
      // BUG 4487899 - table-page-state-manager scheduled tasks are not being executed for landing pages page preventing toolbar render on update
      // Now that PredicateListReadonly is changed to PredicateListUneditable, there's a need to update the toolbar
      // more than once hence the implementation of predicateListReadonlyToolbarUpdated is not good enough.
      // Until BUG 4487899 is fixed - creating this updated implementation to be trigger render after each update.
      updateToolbarBinded = (value) => {
        bind(toolbarEditor.update, toolbarEditor)(value);
        this.predicateListToolbarUpdated(value);
      };
    } else {
      // add a callback predicateListReadOnlyToolbarUpdated so we can update the page when the readonly predicate list is rendered.
      // we've seen race condition that the readonly predicate list is rendered AFTER
      // the last DimensionsPage::render invoke from resizeGroupBase during page initialization,
      // so the + Add Filter becomes invisible
      updateToolbarBinded = compose(this.predicateListReadOnlyToolbarUpdated, bind(toolbarEditor.update, toolbarEditor));
    }

    if (isMultiAccountPage) {
      // initialize account selector button and download for multi-account scenarios
      this.toolbarEditor.update({
        id: 'account-selector-toolbar-item',
        content: <AccountSelector
          {...this.props}
          updateSelected={this.updateSelected}
          getSelectedScope={() => this.state.selectedScope}
          description={this.props.i18n.getString(_TL_('All accounts'))}
        />,
      });

      this.toolbarEditor.update({
        id: 'multiaccount-download-toolbar-item',
        content:
  <Button
    onClick={() => {
      if (this.multiAccountDownloadUrl &&
        this.multiAccountDownloadUrl.length) {
          // eslint-disable-next-line function-paren-newline
        notificationApi.showInfo(this.props.i18n.getString(
          _TL_('Download will be started shortly.')));

        download(this.multiAccountDownloadUrl);
      } else {
        // eslint-disable-next-line function-paren-newline
        notificationApi.showWarning(this.props.i18n.getString(
          _TL_('There was error to fetch report data, no report will be downloaded.')));
      }
    }}
    className={`${this.props.classes.customToolbarItem} download-button`}
  >
    <span className={this.props.classes.downloadIcon} />
    <span className="text">
      {this.props.i18n.getString(_TL_('Download'))}
    </span>
  </Button>,
      });
    }

    if (this.willUnMount) {
      return {};
    }

    const {
      i18n,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      permissions,
      ...filterProps
    } = filterConfig;

    return (<FilterViewManagerWithGlobalFilterContext
      filterService={filterService}
      updateToolbar={updateToolbarBinded}
      resetToolbarVisibility={resetToolbarVisibility}
      legacyI18n={i18n}
      i18n={newI18n}
      permissions={this.permissions}
      {...filterProps}
      // in multi-account mode, do not allow duplicate filter on same property
      disallowDuplicateFilters={isMultiAccountPage || (this.scope.values.aid && this.props.reportType.indexOf('LabelReport') !== -1)}
    />);
  }

  predicateListToolbarUpdated = (value) => {
    if (this.state.predicateListToolbarUpdated !== value) {
      this.setState({
        predicateListToolbarUpdated: value,
      });
    }
  }

  predicateListReadOnlyToolbarUpdated = () => {
    if (!this.state.predicateListReadOnlyToolbarUpdated) {
      this.setState({
        predicateListReadOnlyToolbarUpdated: true,
      });
    }
  }

  updateSelected = (selected) => {
    this.setState({
      selectedScope: selected,
    });
    if (this.state.backboneGridView && this.state.backboneGridView.gridView) {
      this.state.backboneGridView.gridView.refresh(true);
    }

    // update account selector button text
    this.toolbarEditor.update({
      id: 'account-selector-toolbar-item',
      content: <AccountSelector
        {...this.props}
        updateSelected={this.updateSelected}
        getSelectedScope={() => this.state.selectedScope}
        description={selected.AccountIds && selected.AccountIds.length ?
          this.props.i18n.getString(_TL_('Multiple accounts selected')) :
          this.props.i18n.getString(_TL_('All accounts'))
        }
      />,
    });
  }

  updateMultiAccountDownloadUrl = (url) => {
    this.multiAccountDownloadUrl = url;
  };

  createGrid() {
    const {
      toolbarSelectedConfig,
      toolbarUnselectedConfig,
      updateToolbar,
      resetToolbarVisibility,
      serverContext,
      scope,
      preferenceService,
      permissions,
      toolbarPlugin,
      currentCampaign,
      currentAdGroup,
      onGridInjected,
      showTopOfGridPanel,
      reportType,
      perfMarker,
      type,
    } = this.props;

    this.viewport = React.createRef();
    this.willUnMount = false;

    if (this.state && this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }
    if (this.state) {
      this.setState({ backboneGridView: null, filterViewManager: null });
    } else {
      this.state = {
        backboneGridView: null,
        filterViewManager: null,
      };
    }
    const newI18n = this.props.i18n;
    const isMultiAccountPage = !scope.values.aid && reportType !== AccountLabelMccReport.name;

    // if aid is not in scope, create custom data source for customer scope
    const customDataSource = isMultiAccountPage ?
      new DimensionsDataSource({
        scope,
        serverContext,
        reportType,
        getSelectedScope: () => this.state.selectedScope,
        i18n: newI18n,
        updateDownloadUrl: this.updateMultiAccountDownloadUrl.bind(this),
        permissions,
      }) : null;

    const dimensionsGridPromise = createDimensionsGridForUiNext({
      serverContext,
      scope,
      preferenceService,
      permissions,
      toolbarPlugin,
      currentCampaign,
      currentAdGroup,
      viewport: this.viewport.current,
      gridName: 'summary_tab_dimensions',
      gridType: type,
      customDataSource,
      perfMarker,
      willParentViewUnMount: () => this.willUnMount,
    });

    const DimensionsGridView = React.lazy(() => { // eslint-disable-line arrow-body-style
      return dimensionsGridPromise.then(({
        gridView,
        filterService,
        i18n,
        appConfig,
        filterConfig,
      }) => {
        if (this.willUnMount) {
          return {};
        }

        gridView.overrideWith = showTopOfGridPanel; // eslint-disable-line no-param-reassign
        this.filterService = filterService;
        onGridInjected({
          backboneGridView: gridView,
          deselectAll: gridView.grid.gridView.deselectAll.bind(gridView.grid.gridView),
        });
        this.i18n = i18n;

        const filterViewManager = this.getFilterBar({
          filterService,
          filterConfig,
          newI18n,
          toolbarSelectedConfig,
          toolbarUnselectedConfig,
          updateToolbar,
          resetToolbarVisibility,
          isMultiAccountPage,
        });

        this.setState({ backboneGridView: gridView, filterViewManager });

        if (customDataSource) {
          customDataSource.appConfig = appConfig;
        }

        return {
          default: () => (<StyledGridViewWrapperWithFilterContext gridView={gridView} gridClassName="bingadsgrid small-padding-left" />),
        };
      }).bind(this);
    });

    this.dimensionsGrid = (<DimensionsGridView />);
  }

  render() {
    const {
      serverContext: {
        CurrentAccount: { TimeZone },
      },
      panelView,
      renderToolbarContainer,
      showTopGridPanel,
    } = this.props;
    const topGridPanel = showTopGridPanel ? (
      <TopGridPanel
        panelView={panelView}
        customClasses={{
          topGridPanelCustomStyles: this.props.classes.topGridPanelCustomStyles,
        }}
      />) : null;

    const topOfGridContent = (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        {topGridPanel}
      </React.Suspense>
    );

    const stickyTopOfGridContent = (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        {renderToolbarContainer()}
        {this.state.filterViewManager}
      </React.Suspense>
    );

    const isHourOfDayGrid = this.props.reportType === 'TimeHourOfDay';

    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        {isHourOfDayGrid ? this.getHourOfDayErrorMessage() : null}
        <StyledTablePageContainer
          topOfGridContent={topOfGridContent}
          stickyTopOfGridContent={stickyTopOfGridContent}
          gridView={this.dimensionsGrid}
          backboneGridView={this.state.backboneGridView}
          customClasses={{
            tablePageContainer: 'dimensions-page small-padding-left',
            gridViewContainer: classNames(['dimensions-table', this.props.classes.gridCustomStyles]),
          }}
          timeZone={TimeZone}
          scrollToTop={this.props.forceHideToolbar && this.props.showTopGridPanel}
          forceResetScrollFlag={this.props.forceResetScrollFlag}
        />
      </React.Suspense>
    );
  }
}

DimensionsPage.propTypes = {
  type: PropTypes.number.isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.number,
    }),
  }).isRequired,
  serverContext: PropTypes.shape({
    ServicesEndpoints: PropTypes.object.isRequired,
    Permissions: PropTypes.object.isRequired,
    Token: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  permissions: PropTypes.shape({}).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  showTopOfGridPanel: PropTypes.func.isRequired,
  onGridInjected: PropTypes.func.isRequired,
  toolbarSelectedConfig: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  toolbarUnselectedConfig: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  updateToolbar: PropTypes.func.isRequired,
  resetToolbarVisibility: PropTypes.func.isRequired,
  panelView: PropTypes.func.isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  showInlineChart: PropTypes.bool.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  forceHideToolbar: PropTypes.bool.isRequired,
  forceResetScrollFlag: PropTypes.bool.isRequired,
  currentCampaign: PropTypes.shape({}).isRequired,
  currentAdGroup: PropTypes.shape({}).isRequired,
  reportType: PropTypes.string.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

export const StyledDimensionsPage = compose(
  withScope,
  withI18nConsumer,
  WrappedComponent => withDefaultStyles(WrappedComponent, getDimensionsPageStyle),
  withTablePageStateManager({ getToolbarConfig })
)(DimensionsPage);
